import React, { useState, useEffect } from "react";
import { useAuthContext } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { auth } from "@startupp-cloud/startupp-ai-sdk";

const Login = () => {
  const { error, loading, loginWithMethod, authState } = useAuthContext();
  const navigate = useNavigate();
  const [loginMethod, setLoginMethod] = useState(null);

  // Clear login method state when auth state changes
  useEffect(() => {
    if (authState === "authenticated" || authState === "error") {
      setLoginMethod(null);
    }
  }, [authState]);

  const handleLogin = async () => {
    try {
      console.log("[Login] Using redirect authentication method");
      setLoginMethod("redirect");

      // Always use redirect method
      await loginWithMethod("redirect");

      // Navigation will be handled by AuthContext after successful login
    } catch (err) {
      console.error("Login failed:", err);
      setLoginMethod(null);
    }
  };

  const isLoading = loading || loginMethod !== null;

  return (
    <div
      className="custom-login-override"
      style={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div style={{ width: "100%", maxWidth: "400px" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <div
            style={{
              width: "80px",
              height: "80px",
              background: "linear-gradient(135deg, #1e293b, #0f172a)",
              borderRadius: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 1.5rem",
              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
            }}
          >
            <svg
              style={{ width: "40px", height: "40px", color: "white" }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </div>
          <h1
            style={{
              fontSize: "2rem",
              fontWeight: "700",
              color: "#1e293b",
              margin: "0 0 0.5rem",
              letterSpacing: "-0.025em",
            }}
          >
            Gargle Analytics
          </h1>
          <p
            style={{
              fontSize: "1rem",
              color: "#64748b",
              margin: "0 0 0.25rem",
              fontWeight: "500",
            }}
          >
            Internal Marketing Analytics Platform
          </p>
          <p
            style={{
              fontSize: "0.875rem",
              color: "#94a3b8",
              margin: "0",
            }}
          >
            Sign in with your company Google account
          </p>
        </div>

        {/* Login Card */}
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "2rem",
            boxShadow:
              "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            border: "1px solid #e2e8f0",
          }}
        >
          {/* Primary Login Button */}
          <button
            onClick={handleLogin}
            disabled={isLoading}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0.875rem 1.5rem",
              background: isLoading
                ? "#64748b"
                : "linear-gradient(135deg, #1e293b, #0f172a)",
              color: "white",
              border: "none",
              borderRadius: "12px",
              fontSize: "1rem",
              fontWeight: "600",
              cursor: isLoading ? "not-allowed" : "pointer",
              transition: "all 0.2s ease",
              boxShadow: "0 4px 14px 0 rgba(0, 0, 0, 0.15)",
              opacity: isLoading ? 0.7 : 1,
              marginBottom: "1.5rem",
            }}
            onMouseOver={(e) => {
              if (!isLoading) {
                e.target.style.transform = "translateY(-1px)";
                e.target.style.boxShadow = "0 6px 20px 0 rgba(0, 0, 0, 0.2)";
              }
            }}
            onMouseOut={(e) => {
              if (!isLoading) {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 4px 14px 0 rgba(0, 0, 0, 0.15)";
              }
            }}
          >
            {isLoading ? (
              <>
                <div
                  style={{
                    width: "18px",
                    height: "18px",
                    border: "2px solid rgba(255, 255, 255, 0.3)",
                    borderTop: "2px solid white",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    marginRight: "0.5rem",
                  }}
                ></div>
                Redirecting to Google...
              </>
            ) : (
              <>
                <svg
                  style={{
                    width: "18px",
                    height: "18px",
                    marginRight: "0.5rem",
                  }}
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continue with Google
              </>
            )}
          </button>

          {/* Error Message */}
          {error && (
            <div
              style={{
                padding: "0.75rem",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "8px",
                marginBottom: "1rem",
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-start" }}>
                <svg
                  style={{
                    width: "16px",
                    height: "16px",
                    color: "#ef4444",
                    marginRight: "0.5rem",
                    marginTop: "1px",
                    flexShrink: 0,
                  }}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <h3
                    style={{
                      fontSize: "0.875rem",
                      fontWeight: "600",
                      color: "#dc2626",
                      margin: "0 0 0.25rem",
                    }}
                  >
                    Authentication Error
                  </h3>
                  <div style={{ fontSize: "0.875rem", color: "#b91c1c" }}>
                    {error.message ||
                      "Unable to authenticate. Please try again or contact IT support."}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Security Notice */}
          <div
            style={{
              padding: "0.75rem",
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
            }}
          >
            <div style={{ display: "flex", alignItems: "flex-start" }}>
              <svg
                style={{
                  width: "16px",
                  height: "16px",
                  color: "#64748b",
                  marginRight: "0.5rem",
                  marginTop: "1px",
                  flexShrink: 0,
                }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              <div>
                <h3
                  style={{
                    fontSize: "0.875rem",
                    fontWeight: "600",
                    color: "#1e293b",
                    margin: "0 0 0.25rem",
                  }}
                >
                  Secure Access
                </h3>
                <div style={{ fontSize: "0.875rem", color: "#64748b" }}>
                  This platform is restricted to authorized personnel only.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Support Information */}
        <div style={{ textAlign: "center", marginTop: "2rem" }}>
          <p
            style={{
              fontSize: "0.875rem",
              color: "#64748b",
              margin: "0 0 0.75rem",
              fontWeight: "500",
            }}
          >
            Need assistance?
          </p>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "1rem",
            }}
          >
            <a
              href="mailto:it-support@company.com"
              style={{
                color: "#1e293b",
                textDecoration: "none",
                fontSize: "0.875rem",
                fontWeight: "600",
                transition: "color 0.2s ease",
              }}
              onMouseOver={(e) => (e.target.style.color = "#0f172a")}
              onMouseOut={(e) => (e.target.style.color = "#1e293b")}
            >
              IT Support
            </a>
            <span style={{ color: "#cbd5e1" }}>|</span>
            <a
              href="#"
              style={{
                color: "#1e293b",
                textDecoration: "none",
                fontSize: "0.875rem",
                fontWeight: "600",
                transition: "color 0.2s ease",
              }}
              onMouseOver={(e) => (e.target.style.color = "#0f172a")}
              onMouseOut={(e) => (e.target.style.color = "#1e293b")}
            >
              User Guide
            </a>
          </div>
        </div>
      </div>

      <style jsx="true">{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default Login;
